// Processamento automático de imagens antes do upload.
// Agnóstico à plataforma (web, iOS e Android via WebView do Capacitor):
// browser-image-compression usa apenas Canvas/Web APIs disponíveis no WebView.
//
// Padrão: redimensiona para no máx. 1920px (lado maior) e converte para
// image/webp (qualidade 0.8). PDFs e outros não-imagem passam direto.
import imageCompression from "browser-image-compression";
import api from "./api";

const DEFAULT_OPTS = {
  maxWidthOrHeight: 1920, // limita largura OU altura a 1920px
  fileType: "image/webp", // padroniza o formato de saída
  initialQuality: 0.8, // qualidade 80%
  useWebWorker: true, // não trava a UI durante o processamento
};

// Formatos que NÃO devem ser recomprimidos (perderiam animação / não são imagem)
const PASSTHROUGH_TYPES = new Set(["image/gif"]);

// V18.9.4 — Limite defensivo para PDFs no upload (evita bater no limite do ingress de produção)
// O backend aceita até 15MB, mas alguns ingress/proxies (nginx default) rejeitam
// payloads acima de 4-8MB com um erro que aparece como "falha de conexão" no cliente.
const PDF_MAX_MB = 8;
const PDF_MAX_BYTES = PDF_MAX_MB * 1024 * 1024;

const isImage = (file) =>
  !!file && typeof file.type === "string" && file.type.startsWith("image/");

const isPdf = (file) =>
  !!file && (
    (typeof file.type === "string" && file.type.toLowerCase() === "application/pdf") ||
    (typeof file.name === "string" && file.name.toLowerCase().endsWith(".pdf"))
  );

/**
 * Comprime/padroniza uma imagem para WEBP (máx. 1920px, qualidade 0.8).
 * Não-imagens (ex.: PDF) e GIFs são retornados sem alteração.
 * Lança erro se a compressão de uma imagem falhar — assim a ORIGINAL
 * (pesada) nunca é enviada silenciosamente.
 * @param {File|Blob} file
 * @param {object} [opts] sobrescreve as opções padrão
 * @returns {Promise<File>}
 */
export async function compressImage(file, opts = {}) {
  if (!isImage(file) || PASSTHROUGH_TYPES.has(file.type)) return file;

  const compressed = await imageCompression(file, { ...DEFAULT_OPTS, ...opts });
  const baseName = (file.name || "imagem").replace(/\.[^.]+$/, "");
  // O backend decide o tipo pela extensão do filename → precisa terminar em .webp
  return new File([compressed], `${baseName}.webp`, {
    type: "image/webp",
    lastModified: Date.now(),
  });
}

/**
 * Fluxo completo: processa a imagem (resize + webp) e envia o Blob comprimido
 * ao backend via FormData. Garante que a imagem original não seja enviada.
 * @param {File|Blob} file arquivo escolhido pelo usuário (input/câmera)
 * @param {object} [options]
 * @param {string} [options.field="file"] nome do campo no FormData
 * @param {object} [options.compressOptions] opções para o compressor
 * @param {AbortSignal} [options.signal] para cancelar o upload
 * @param {(progress:number)=>void} [options.onProgress] 0..100
 * @returns {Promise<object>} resposta do backend (ex.: { path })
 */
export async function processAndUploadImage(file, options = {}) {
  const { field = "file", compressOptions = {}, signal, onProgress, kind } = options;

  if (!file) throw new Error("Nenhum arquivo selecionado.");

  // V18.9.4 — Guard client-side para PDFs. Muitos ingress/proxies (nginx padrão)
  // rejeitam bodies > ~4-8MB de forma que o navegador só reporta "network error".
  // Preferimos falhar aqui com mensagem clara do que ver "sem conexão".
  if (isPdf(file) && typeof file.size === "number" && file.size > PDF_MAX_BYTES) {
    throw new Error(
      `PDF muito grande (${(file.size / 1024 / 1024).toFixed(1)}MB). O limite é ${PDF_MAX_MB}MB — tente reduzir o arquivo.`,
    );
  }

  let processed;
  try {
    processed = await compressImage(file, compressOptions);
  } catch (err) {
    console.error("[imageUpload] falha ao processar a imagem:", err);
    throw new Error(
      "Não foi possível processar esta imagem. Tente outra foto ou formato.",
    );
  }

  const fd = new FormData();
  // V18.9.4 — Nome de arquivo defensivo: se o picker nativo devolveu um File sem
  // .name (comum em Android via SAF/content://), inferimos a extensão pelo MIME
  // pra evitar cair no fallback ".webp" que confunde a validação de tamanho no backend.
  let safeName = processed.name && String(processed.name).trim();
  if (!safeName) {
    if (isPdf(processed)) safeName = "documento.pdf";
    else if (typeof processed.type === "string" && processed.type.startsWith("image/")) {
      const ext = processed.type.split("/")[1] || "jpg";
      safeName = `upload.${ext === "jpeg" ? "jpg" : ext}`;
    } else safeName = "upload.bin";
  }
  fd.append(field, processed, safeName);

  try {
    const { data } = await api.post("/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      // V18.9.6 — envia `?kind=kyc` para o backend marcar o arquivo como PRIVADO
      // (só o dono ou admin podem baixar depois via /api/files/{path}).
      params: kind ? { kind } : undefined,
      signal,
      // V18.9.4 — Timeout de 60s: uploads grandes em 4G podem demorar; sem esse
      // limite explícito o axios espera indefinidamente e o app "trava".
      timeout: 60000,
      onUploadProgress: onProgress
        ? (e) => {
            if (e.total) onProgress(Math.round((e.loaded / e.total) * 100));
          }
        : undefined,
    });
    return data;
  } catch (err) {
    console.error("[imageUpload] falha no upload:", err);
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string") throw new Error(detail);
    // V18.9.4 — Mensagens de erro específicas p/ cada categoria (não é sempre "sem internet")
    const status = err?.response?.status;
    if (status === 413) {
      throw new Error("Arquivo maior do que o servidor aceita. Tente reduzir o tamanho.");
    }
    if (err?.code === "ECONNABORTED" || (err?.message || "").toLowerCase().includes("timeout")) {
      throw new Error("O upload demorou demais. Tente novamente em uma rede melhor.");
    }
    if (!err?.response) {
      // Sem resposta HTTP: pode ser rede caída, mas também ingress bloqueando payload grande.
      const sizeMb = file?.size ? (file.size / 1024 / 1024).toFixed(1) : null;
      throw new Error(
        sizeMb
          ? `Falha no envio (arquivo ${sizeMb}MB). Verifique a conexão ou tente um arquivo menor.`
          : "Falha no envio. Verifique sua conexão e tente novamente.",
      );
    }
    throw new Error("Falha ao enviar o arquivo. Tente novamente.");
  }
}

/**
 * Sobe a imagem em DUAS versões: a cheia (máx. 1920px, q0.8) e uma MINIATURA
 * (máx. 500px, q0.85 — nítida e leve) para usar nas listagens/cards.
 * Não-imagens (PDF) sobem só uma vez e retornam thumb = null.
 * Se a miniatura falhar, retorna thumb = null (o caller faz fallback p/ a cheia).
 * @param {File|Blob} file
 * @param {object} [options]
 * @param {object} [options.fullOptions] opções da imagem cheia
 * @param {number} [options.thumbMaxSize=500]
 * @param {number} [options.thumbQuality=0.85]
 * @param {AbortSignal} [options.signal]
 * @param {(p:number)=>void} [options.onUploadProgress] 0..100 (upload da imagem cheia)
 * @param {(p:number)=>void} [options.onThumbProgress]  0..100 (upload da miniatura)
 * @returns {Promise<{path:string, thumb:(string|null)}>}
 */
export async function processAndUploadImagePair(file, options = {}) {
  const {
    fullOptions = {}, thumbMaxSize = 500, thumbQuality = 0.85, signal,
    onUploadProgress, onThumbProgress,
  } = options;
  if (!file) throw new Error("Nenhum arquivo selecionado.");

  // Não-imagem (ex.: PDF): sobe uma vez só, sem miniatura.
  if (!isImage(file) || PASSTHROUGH_TYPES.has(file.type)) {
    const data = await processAndUploadImage(file, { signal, onProgress: onUploadProgress });
    return { path: data.path, thumb: null };
  }

  const full = await processAndUploadImage(file, {
    compressOptions: fullOptions, signal, onProgress: onUploadProgress,
  });
  let thumb = null;
  try {
    const thumbData = await processAndUploadImage(file, {
      compressOptions: { maxWidthOrHeight: thumbMaxSize, initialQuality: thumbQuality },
      signal,
      onProgress: onThumbProgress,
    });
    thumb = thumbData.path;
  } catch (err) {
    console.warn("[imageUpload] miniatura falhou; usaremos a imagem cheia:", err);
  }
  return { path: full.path, thumb };
}

export default processAndUploadImage;
